import { type CreateProperty, type UpdateProperty } from '@flipdish/orgmanagement';
import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import type { NotifyProps } from '../../../layouts/Notify/actions';
import { getAssociationsKey } from '../../RMS/rms.services';
import { createPropertyForOrg, updatePropertyById } from '../properties.services';

export interface PropertyMutationError {
  readonly message: string;
  readonly code: string;
}

export interface UsePropertyMutationsArgs {
  readonly orgId: string;
  readonly propertyId: string;
  readonly appId: string;
  readonly dispatchCloseNotifySaving: () => void;
  readonly dispatchNotifyError: (data: NotifyProps) => void;
  readonly dispatchNotifySaved: () => void;
}

export interface UsePropertyMutationsResult {
  readonly createPropertyMutation: UseMutationResult<
    unknown,
    PropertyMutationError,
    CreateProperty
  >;
  readonly updatePropertyMutation: UseMutationResult<
    unknown,
    PropertyMutationError,
    UpdateProperty
  >;
}

export const usePropertyMutations = (
  args: UsePropertyMutationsArgs
): UsePropertyMutationsResult => {
  const {
    orgId,
    propertyId,
    appId,
    dispatchCloseNotifySaving,
    dispatchNotifyError,
    dispatchNotifySaved,
  } = args;

  const history = useHistory();
  const queryClient = useQueryClient();

  const createPropertyMutation = useMutation<unknown, PropertyMutationError, CreateProperty>({
    mutationFn: (newProperty: CreateProperty) => createPropertyForOrg(orgId, newProperty),
    onSuccess: () => {
      dispatchCloseNotifySaving();
      dispatchNotifySaved();
      queryClient.invalidateQueries({ queryKey: [getAssociationsKey] });
      history.push(`/${appId}/properties`);
    },
    onError: (error: PropertyMutationError) => {
      dispatchCloseNotifySaving();
      if (error.code === 'Property_name_already_exists') {
        dispatchNotifyError({ message: error.code, translate: true });
      } else {
        dispatchNotifyError({ message: 'Failed_to_create_property', translate: true });
      }
    },
  });

  const updatePropertyMutation = useMutation<unknown, PropertyMutationError, UpdateProperty>({
    mutationFn: (updatedProperty: UpdateProperty) =>
      updatePropertyById(orgId, propertyId, updatedProperty),
    onSuccess: () => {
      dispatchCloseNotifySaving();
      dispatchNotifySaved();
      queryClient.invalidateQueries({ queryKey: [getAssociationsKey] });
      queryClient.invalidateQueries({ queryKey: ['getPropertyById'] });
    },
    onError: (error: PropertyMutationError) => {
      dispatchCloseNotifySaving();
      if (error.code === 'Property_name_already_exists') {
        dispatchNotifyError({ message: error.code, translate: true });
      } else {
        dispatchNotifyError({ message: 'Failed_to_update_property', translate: true });
      }
    },
  });

  return { createPropertyMutation, updatePropertyMutation };
};
